/**
 * Environment Configuration
 *
 * This file centralizes all environment variable access and provides
 * defaults for development. For production, set these variables in your
 * deployment environment.
 *
 * Based on Vite's environment variable documentation:
 * https://vite.dev/guide/env-and-mode.html#env-variables-and-modes
 */

export interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string
  fleshDomain: string

  // Authentication
  authCookieDomain: string

  // Application
  appTitle: string
  nodeEnv: string

  // Environment checks
  isDevelopment: boolean
  isProduction: boolean
  isTest: boolean
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, defaultValue: string): string {
  return import.meta.env[key] || defaultValue
}

/**
 * Environment configuration with defaults
 */
export const env: EnvironmentConfig = {
  // API Configuration
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:3000'),
  fleshDomain: getEnvVar('VITE_FLESH_DOMAIN', 'http://localhost:3001'),

  // Authentication
  authCookieDomain: getEnvVar('VITE_AUTH_COOKIE_DOMAIN', 'localhost'),

  // Application
  appTitle: getEnvVar('VITE_APP_TITLE', 'Reporting'),
  nodeEnv: getEnvVar('VITE_NODE_ENV', import.meta.env.MODE || 'development'),

  // Environment checks
  get isDevelopment() {
    return this.nodeEnv === 'development' || import.meta.env.DEV
  },

  get isProduction() {
    return this.nodeEnv === 'production' || import.meta.env.PROD
  },

  get isTest() {
    return this.nodeEnv === 'test'
  },
}

/**
 * Validate required environment variables
 */
export function validateEnvironment(): void {
  const requiredVars = [
    'apiBaseUrl',
    'fleshDomain',
    'authCookieDomain',
  ] as const

  const missing = requiredVars.filter(key => !env[key])

  if (missing.length > 0) {
    console.warn(
      `⚠️  Missing environment variables: ${missing.join(', ')}\n` +
        'Using default values. For production, ensure these are set.'
    )
  }
}

/**
 * Log current environment configuration (development only)
 */
export function logEnvironmentConfig(): void {
  if (env.isDevelopment) {
    console.log('🔧 Environment Configuration:', {
      mode: import.meta.env.MODE,
      nodeEnv: env.nodeEnv,
      apiBaseUrl: env.apiBaseUrl,
      fleshDomain: env.fleshDomain,
      authCookieDomain: env.authCookieDomain,
      isDevelopment: env.isDevelopment,
      isProduction: env.isProduction,
    })
  }
}

// Auto-validate on import
validateEnvironment()
